import { useEffect, useState } from "react";
import { useParams } from "react-router";
import { renderMarkdown } from "@/lib/markdown";

interface SignData { title: string; body: string | null; status: string; signerName: string; signerEmailMasked: string; accountName: string; consentText: string; signedAt: string | null; sha256: string | null; version: number }

export function PublicSignPage() {
  const { token } = useParams();
  const [d, setD] = useState<SignData | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [code, setCode] = useState("");
  const [codeSent, setCodeSent] = useState(false);
  const [typed, setTyped] = useState("");
  const [consent, setConsent] = useState(false);
  const [busy, setBusy] = useState(false);
  const load = () => fetch(`/api/public/sign/${token}`).then(async (r) => { const j = await r.json(); if (!r.ok) throw new Error(j.error); setD(j); }).catch((e) => setErr(e.message));
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [token]);
  const post = async (path: string, body: unknown) => {
    setBusy(true); setErr(null);
    try { const r = await fetch(`/api/public/sign/${token}/${path}`, { method: "POST", headers: { "content-type": "application/json" }, body: JSON.stringify(body) }); const j = await r.json(); if (!r.ok) throw new Error(j.error ?? "Something went wrong"); return j; }
    catch (e) { setErr((e as Error).message); return null; } finally { setBusy(false); }
  };
  return (
    <div className="public">
      <div className="wrap">
        <div className="brand"><span style={{ display: "inline-flex", alignItems: "center", background: "#070709", borderRadius: 8, padding: "8px 14px" }}><img src="/assets/hixonops-wordmark.png" alt="HixonOps" style={{ height: 24 }} /></span></div>
        <div className="sheet">
          {err && !d && <div className="err">{err}</div>}
          {d && (
            <>
              <h1>{d.title}</h1>
              <p className="muted">For {d.accountName} · prepared for {d.signerName} · v{d.version}</p>
              {d.status === "signed" ? (
                <div style={{ marginTop: 16 }}>
                  <div className="ok">Signed on {new Date(d.signedAt!).toLocaleString()}. A copy was emailed to HixonOps. SHA-256 of the signed PDF: <code style={{ fontSize: 11, wordBreak: "break-all" }}>{d.sha256}</code></div>
                  <p style={{ marginTop: 12 }}><a className="btn" href={`/api/public/sign/${token}/pdf`} target="_blank" rel="noreferrer">Download signed PDF</a></p>
                </div>
              ) : d.status === "pending" ? (
                <div style={{ marginTop: 16, display: "flex", flexDirection: "column", gap: 12 }}>
                  <div className="notice">To protect this document, confirm you control <strong>{d.signerEmailMasked}</strong>. We will email you a 6-digit code.</div>
                  {err && <div className="err">{err}</div>}
                  {!codeSent ? <div><button className="btn primary" disabled={busy} onClick={async () => { if (await post("request-code", {})) setCodeSent(true); }}>Email me a code</button></div> : (
                    <div className="form-grid" style={{ alignItems: "end" }}>
                      <div className="field"><label>Verification code</label><input className="input" inputMode="numeric" autoFocus value={code} onChange={(e) => setCode(e.target.value)} /></div>
                      <div style={{ display: "flex", gap: 8 }}><button className="btn primary" disabled={busy || code.length < 6} onClick={async () => { if (await post("verify", { code })) load(); }}>Verify</button><button className="btn" disabled={busy} onClick={() => post("request-code", {})}>Resend</button></div>
                    </div>
                  )}
                </div>
              ) : (
                <div style={{ marginTop: 16, display: "flex", flexDirection: "column", gap: 14 }}>
                  <div className="ok">Email verified. Please read the agreement below.</div>
                  <div className="doc" dangerouslySetInnerHTML={{ __html: renderMarkdown(d.body ?? "") }} />
                  {err && <div className="err">{err}</div>}
                  <label className="checkrow"><input type="checkbox" checked={consent} onChange={(e) => setConsent(e.target.checked)} /><span>{d.consentText}</span></label>
                  <div className="field"><label>Type your full name to sign</label><input className="input" style={{ fontFamily: "Georgia, serif", fontSize: 20, fontStyle: "italic", height: 48 }} value={typed} onChange={(e) => setTyped(e.target.value)} placeholder={d.signerName} /></div>
                  <div><button className="btn primary" disabled={busy || !consent || typed.trim().length < 2} onClick={async () => { if (await post("sign", { typedSignature: typed, consent })) load(); }}>Sign agreement</button></div>
                </div>
              )}
            </>
          )}
        </div>
        <p className="muted" style={{ fontSize: 12, marginTop: 16 }}>HixonOps · Missoula, MT · Questions? Reply to the email that brought you here.</p>
      </div>
    </div>
  );
}
